import type { FieldDef } from '~/components/form/utils';
import type { Car, PropertyForm, ServiceProviderGroup } from '~/data/generated/model';
import type { InvoiceKindKey } from '~/data/schemas/invoice';
import {
  InvoiceKind,
  invoiceKindOptions,
  invoiceKindToLabelMap,
  invoiceTypeOptions,
} from '~/data/schemas/invoice';
import type { UserDisplay } from '~/data/schemas/user';
import { LocaleTerms } from '~/hooks/useLocaleTerms';

type CarForForm = Pick<Car, 'id' | 'name' | 'ahjId'>;

interface InvoiceFieldDefOptions {
  properties: PropertyForm[] | undefined;
  consultants: UserDisplay[] | undefined;
  kind: InvoiceKindKey;
  cars: CarForForm[] | undefined;
  serviceProviders: ServiceProviderGroup[] | undefined;
  terms: LocaleTerms;
  showKind?: boolean;
}

export const buildFieldDef = ({
  properties,
  consultants,
  kind,
  cars,
  serviceProviders,
  terms,
  showKind = false,
}: InvoiceFieldDefOptions) => {
  const invoiceOrProposal = invoiceKindToLabelMap.get(kind);
  const kindField: FieldDef = {
    name: 'kind',
    label: 'Kind',
    type: 'select',
    options: invoiceKindOptions.map(([value, label]) => ({ id: value, label })),
  };
  const generalFields: FieldDef[] = [
    ...(showKind ? [kindField] : []),
    { name: 'date', label: 'Date', type: 'date' },
    {
      name: 'number',
      label: `${invoiceOrProposal} #`,
      type: 'text',
    },
    {
      name: 'propertyId',
      label: 'Property',
      type: 'autocomplete',
      options:
        properties
          ?.filter((property) => property.id)
          .map((property) => ({
            id: property.id as string,
            label: property.name,
          })) ?? [],
      loading: properties === undefined,
      gridProps: { xs: 12 },
    },
    {
      name: 'consultantId',
      label: 'Consultant',
      type: 'autocomplete',
      options:
        consultants?.map((consultant) => ({
          id: consultant.id,
          label: consultant.fullName || consultant.email || '',
        })) ?? [],
      loading: consultants === undefined,
    },
    {
      name: 'type',
      label: 'Type',
      type: 'select',
      options: invoiceTypeOptions.map(([value, label]) => ({ id: value, label })),
    },
    {
      name: 'carIds',
      label: terms.vehiclesServiced,
      type: 'autocomplete-multiple',
      options: cars
        ? cars.map((car) => ({
            id: car.id,
            label: car.name + (car.ahjId ? ` - ${car.ahjId}` : ''),
          }))
        : [],
      loading: cars === undefined,
    },
    {
      name: 'serviceProviderId',
      label: 'Service Provider',
      type: 'autocomplete',
      options: serviceProviders
        ? serviceProviders?.map((provider) => ({
            id: provider.id,
            label: provider.name,
          }))
        : [],
      loading: serviceProviders === undefined,
    },
    {
      name: 'description',
      label: 'Description',
      type: 'text-multiline',
      required: false,
      gridProps: { xs: 12 },
    },
  ];

  // A credit takes its type from what it applies to and names no cars.
  const creditOmits = new Set(['type', 'carIds']);
  const fieldsForKind =
    kind === InvoiceKind.Credit
      ? generalFields.filter((field) => !creditOmits.has(field.name))
      : generalFields;

  const itemsFields: FieldDef[] = [
    { name: 'lineItems', label: 'Line Items', type: 'line-items', gridProps: { xs: 12 } },
  ];

  const totalField: FieldDef = {
    name: 'proposedAmount',
    label: `${invoiceOrProposal} Amount`,
    type: 'currency',
  };

  return {
    generalFields: fieldsForKind,
    itemsFields,
    totalField,
  };
};
